import React, { useState, useEffect } from 'react';
import { format as formatDate } from 'date-fns';
import { OverlayTrigger, Popover, Button } from 'react-bootstrap';
import { sortFrostProbeTypeData } from '@awp/helpers/utilsReact';
import { format as formatNumber } from '@awp/helpers/numFormat';
import { isValueEmpty, isIntStr, fieldSorter } from '@awp/helpers/utilsReact';
import { sortDataTablesByNumericThenAlpha } from '@awp/helpers/sorter';
import { downloadFile } from '@awp/utils/file-download';
import { SENSOR_PLACEMENT_CONFIG } from '@awp/constants';
import isPlainObject from 'lodash.isplainobject';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import orderBy from 'lodash.orderby';

export const renderValue = (value, cell, key, flags) => {
  const { showInvalidData, showConfidenceLevel, showValidData } = flags;
  let conVal = value;

  conVal = isValueEmpty(cell.DisplayedMeaning) ? conVal : conVal + ' ' + cell.DisplayedMeaning;
  conVal = cell.IsMissing ? 'Missing' : conVal;
  conVal = cell.IsNan ? 'NaN' : conVal;

  if (cell.IsInvalid || cell.IsMissing || cell.IsNan) {
    return showInvalidData ? (
      <OverlayTrigger
        key={key}
        placement="left"
        overlay={
          <Popover id={`popover_${key}`}>
            <Popover.Content>
              <p className="text-left m-0 p-0">
                {cell.ConfidenceLevelName}
                <br />
                Confidence Level <b>{cell.ConfidenceLevel}</b>
                <br />
              </p>
            </Popover.Content>
          </Popover>
        }
      >
        <div>
          <span>{conVal}</span>&nbsp;
          <b className="badge badge-secondary invalid-value">{showConfidenceLevel && <>{cell.ConfidenceLevel}</>}</b>
          &nbsp;
          <span className="badge badge-danger">!</span>
        </div>
      </OverlayTrigger>
    ) : (
      <></>
    );
  } else if (showValidData) {
    return <span>{conVal}</span>;
  } else {
    return <></>;
  }
};

const headerDetails = (dataset) => {
  const unitLabel = dataset.unitLabel === '' ? '' : `[${dataset.unitLabel}]`;

  if (!dataset?.graphConfig?.yAxisTickList) return unitLabel;

  return (
    <>
      {unitLabel}
      <OverlayTrigger
        key={dataset.DatasetId}
        placement="left"
        overlay={
          <Popover id={`popover_${dataset.DatasetId}`}>
            <Popover.Content>
              <p className="text-left m-0 p-0">Click data cell to see legend</p>
            </Popover.Content>
          </Popover>
        }
      >
        <span className="mx-1 badge badge-primary">i</span>
      </OverlayTrigger>
    </>
  );
};

export const buildTableColumns = (datasets, flags) => {
  if (!datasets) return [];
  console.log(datasets);
  let headers = [
    {
      Header: 'Timestamp',
      accessor: 'TimeStamp',
      //sortType: sortDataTablesByNumericThenAlpha, // do not impl this line
    },
  ];

  let group = '';
  let columns = null;
  const _headers = [];

  datasets.forEach((dataset) => {
    if (dataset.group !== group) {
      if (columns) {
        _headers.push({
          Header: group,
          columns,
        });
      }
      columns = [];
      group = dataset.group;
    }
    let _header = (
      <>
        {dataset.label} <br /> {headerDetails(dataset)}
      </>
    );

    //must use sensor type to check if fp or sp
    if (SENSOR_PLACEMENT_CONFIG.MAPPINGS.includes(dataset.SensorTypeName)) {
      if (!isValueEmpty(dataset.RelativeElevation)) {
        _header = (
          <>
            {dataset.label + ' cm'} <br /> {headerDetails(dataset)}
          </>
        );
      } else {
        _header = (
          <>
            {dataset.label} <br /> {headerDetails(dataset)}
          </>
        );
      }
    }

    columns.push({
      ...dataset,
      Header: _header,
      accessor: dataset.DatasetId,
      sortType: sortDataTablesByNumericThenAlpha,
      Cell: ({ value }) => {
        if (isValueEmpty(value)) return '';
        if (value.DisplayFormat && value.DisplayFormat.length > 0) {
          if (isIntStr(value.Value)) {
            return renderValue(formatNumber(value.Value, value.DisplayFormat), value, dataset.DatasetId, flags);
          }
        }

        //if(isIntStr(value.Value)) return renderValue(formatNumber(value.Value, value.DisplayFormat), value, dataset.DatasetId);
        return renderValue(value.Value, value, dataset.DatasetId, flags);
      },
      legend: dataset?.graphConfig?.yAxisTickList,
      rawlabel: dataset?.rawlabel,
    });
  });

  if (columns) {
    _headers.push({
      Header: group,
      columns,
    });
  }

  _headers.sort(fieldSorter(['Header'])).forEach((dataset) => {
    if (dataset && dataset.columns && dataset.columns.length > 1) dataset.columns.sort(fieldSorter(['rawlabel']));
    headers.push(dataset);
  });

  //need to combine headers that are the same
  headers = headers.reduce((accumulator, currentItem) => {
    const existingItem = accumulator.find((item) => item.Header === currentItem.Header);

    if (existingItem) {
      existingItem.columns.push(...currentItem.columns);
      existingItem.columns.sort((a, b) => a.rawlabel.localeCompare(b.rawlabel)); // Sort columns
    } else {
      accumulator.push({ ...currentItem });
    }

    return accumulator;
  }, []);

  return headers;
};

export const buildTableData = (datasets) => {
  const tableData = [];
  const rows = {};

  if (!datasets) return tableData;

  datasets.forEach((dataset) => {
    if (!dataset.Data) return;

    dataset.Data.forEach((cell, j) => {
      if (!rows[cell.x]) {
        rows[cell.x] = {
          TimeStamp: formatDate(new Date(parseInt(cell.x)), 'yyyy-MM-dd HH:mm'),
          [dataset.DatasetId]: cell,
        };
      } else {
        rows[cell.x][dataset.DatasetId] = cell;
      }
    });
  });

  Object.keys(rows).forEach((key) => {
    tableData.push(rows[key]);
  });

  return tableData.sort(fieldSorter(['TimeStamp']));
};

const currentTableData = {
  rows: null,
  columns: null,
  key: '',
};

export const prepareTable = (datasets, flags, key) => {
  //setup the sort name in the dataset options
  console.log(datasets);
  let sortedDatasets = datasets?.map((ds) => {
    return { ...ds, sortName: ds.rawlabel + '_' + ds.PlacementName };
  });

  sortedDatasets = sortFrostProbeTypeData(sortedDatasets, 'SortName');

  const tableColumns = buildTableColumns(sortedDatasets, flags);
  const tableData = buildTableData(sortedDatasets);

  currentTableData.rows = tableData;
  currentTableData.columns = tableColumns;
  currentTableData.key = key;

  return { tableData, tableColumns };
};

const formatTableValue = (val, options) => {
  const { showConfidenceLevel = true, showInvalidData = true, showValidData = true } = options;

  const confidenceLevel = showConfidenceLevel ? ` (${val.ConfidenceLevel})` : '';

  if (!isPlainObject(val)) {
    return String(val);
  }

  if (val.IsMissing) {
    return showInvalidData ? `Missing${confidenceLevel} !` : '';
  }

  if (val.IsNan) {
    return showInvalidData ? `NaN${confidenceLevel} !` : '';
  }

  let text = val.DisplayFormat ? formatNumber(val.Value, val.DisplayFormat) : val.Value;
  text = isEmpty(val.DisplayedMeaning) ? text : text + ' ' + val.DisplayedMeaning;

  if (val.IsInvalid) {
    return showInvalidData ? `${text}${confidenceLevel} !` : '';
  }

  return showValidData ? text : '';
};

const formatSubHeader = (col) => {
  const suffix = col.unitLabel === '' ? '' : `[${col.unitLabel}]`;

  if (SENSOR_PLACEMENT_CONFIG.MAPPINGS.includes(col.SensorTypeName)) {
    if (!isValueEmpty(col.RelativeElevation)) {
      return `${col.label} cm ${suffix}`;
    } else {
      return `${col.label} ${suffix}`;
    }
  }

  return `${col.label} ${suffix}`;
};

const downloadTableData = (props) => {
  const { showConfidenceLevel = true, showInvalidData = true, showValidData = true } = props;

  const headers = [];
  const accessors = [];

  currentTableData.columns.forEach(({ Header, accessor, columns }) => {
    if (accessor) {
      headers.push(Header);
      accessors.push(accessor);
    } else if (columns) {
      columns.forEach((col) => {
        headers.push(`${Header}: ${formatSubHeader(col)}`);
        accessors.push(col.accessor);
      });
    }
  });

  const orderedRows = orderBy(currentTableData.rows, [({ TimeStamp }) => new Date(TimeStamp)], ['desc']);
  const body = orderedRows.map((row) => {
    return accessors.map((accessor) => {
      if (isNil(row[accessor])) return '';
      return formatTableValue(row[accessor], { showConfidenceLevel, showInvalidData, showValidData });
    });
  });

  const rows = [headers, ...body];
  const csvContent = rows
    .map((row) =>
      row
        .map((cell) =>
          cell.includes(',') || cell.includes('"') || cell.includes('\n')
            ? `"${cell.replace(/"/g, '""')}"` // Escape quotes by doubling them
            : cell,
        )
        .join(','),
    )
    .join('\n');

  const csvBlob = new Blob([csvContent], { type: 'text/csv' });
  downloadFile(csvBlob, 'download.csv', {}, 'text/csv');
};

export const TableDataDownloadButton = (props) => {
  const { tableKey } = props;

  const [visible, setVisible] = useState(currentTableData.key === tableKey);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(currentTableData.key === tableKey && currentTableData.rows && currentTableData.rows.length > 0);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <Button
      variant="outline-secondary"
      className="mr-2"
      onClick={() => {
        downloadTableData(props);
      }}
    >
      Download CSV
    </Button>
  );
};
